import { Asset } from '@guiker/asset-shared'
import { EntityDocument, PhoneNumber } from '@guiker/base-entity'
import { PayoutMethod } from '@guiker/payout-shared'
import { NEWPropertyOwnership, RepresentativeRole, WithPropertyOwnership } from '@guiker/real-estate-shared'

import { RentalServiceFeeByLeaseType } from './base'

export { RepresentativeRole }

export enum AgreementStatus {
  'CREATED' = 'CREATED',
  'SENT' = 'SENT',
  'SIGNED' = 'SIGNED',
}

export enum AgreementType {
  'TENANT_FINDER' = 'TENANT_FINDER',
  'FULL_MANAGEMENT' = 'FULL_MANAGEMENT',
  'PRODUCT_ONLY' = 'PRODUCT_ONLY',
}

export enum AgreementFeeType {
  'RENT_GUARANTEE' = 'RENT_GUARANTEE',
  'PERCENTAGE' = 'PERCENTAGE',
}

export type AgreementFee = {
  type: AgreementFeeType
  value: number
}

export type Agreement = {
  status?: AgreementStatus
  type?: AgreementType
  onboarding: {
    started: string // date
    expired?: string // date
  }
  assets?: Asset[]
  fee?: RentalServiceFeeByLeaseType
  contract?: {
    id: number | string
  }
}

export enum ManagerRole {
  OWNER = 'OWNER',
  MANAGEMENT = 'MANAGEMENT',
  AGENT = 'AGENT',
}

export enum RentalRules {
  SMALL_ANIMAL_ALLOWED = 'SMALL_ANIMAL_ALLOWED',
  LARGE_ANIMAL_ALLOWED = 'LARGE_ANIMAL_ALLOWED',
  SMOKING_ALLOWED = 'SMOKING_ALLOWED',
}

export enum Utilities {
  WIFI = 'WIFI',
  HEATING = 'HEATING',
  WATER = 'WATER',
  ELECTRICITY = 'ELECTRICITY',
}

export enum Appliances {
  AIR_CONDITIONER = 'AIR_CONDITIONER',
  DISH_WASHER = 'DISH_WASHER',
  DRYER_LAUNDRY = 'DRYER_LAUNDRY',
  OVEN = 'OVEN',
  REFRIGERATOR = 'REFRIGERATOR',
  STOVE = 'STOVE',
  WASHER_LAUNDRY = 'WASHER_LAUNDRY',
}

export type Furnitures = {
  table: {
    count: number
  }
  chair: {
    count: number
  }
  chestOfDrawer: {
    count: number
  }
  couch: {
    count: number
  }
  armchair: {
    count: number
  }
  bed: {
    count: number
    sizes: []
  }
}

export type PropRentalUnitConfiguration = {
  appliances: Appliances[]
  rules: RentalRules[]
  utilities: Utilities[]
  furnishing: {
    isFurnished: boolean
    furnitures: Furnitures
  }
  availability: {
    availableFrom: string
    price: number
    leaseTerm: number
  }
}

export type Manager = {
  userId: string
  officeId?: string
  firstName: string
  lastName?: string
  title?: string
  emailAddress: string
  phone?: PhoneNumber
  isContactPerson: boolean
  role?: ManagerRole
}

export type PropRental = EntityDocument & {
  propertyOwnership: NEWPropertyOwnership
  payoutMethod?: PayoutMethod
  managers: Manager[]
  agreement?: Agreement
  assets?: Asset[]
  meta?: {
    legacyIds: number[]
  }
  updatedAt?: string
}

export type PropRentalWriteModel = Omit<PropRental, 'propertyOwnership'> & {
  propertyOwnership: {
    id: string
  }
  assets?: {
    id: string
  }[]
}

export type PropRentalProjection = WithPropertyOwnership<PropRental>
